import axios, { AxiosInstance } from "axios";
import qs from "qs";
import { LocalStorageApp } from "./localstorage";
import { toast } from "@/store";
import { TYPE } from "@/store/modules/toast.module";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface TaskResponse<T = any> {
  error?: boolean;
  success?: boolean;
  message?: string;
  data?: T;
}

/** Thin axios wrapper shared by every service — single instance, single auth token, single request-shape helper. */
export class Http {
  /** The single axios instance used for every backend call in the app. */
  static instance: AxiosInstance = axios.create({});

  /** Caps redirects and re-applies the stored auth token — call after a fresh login to make the instance ready for authenticated calls. */
  static sync(): AxiosInstance {
    this.instance.defaults.maxRedirects = 2;
    this.addToken();
    return this.instance;
  }

  /** Serializes an object to `application/x-www-form-urlencoded` form, as the backend's `?task=` endpoints expect. */
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  static qsStringify(param: any) {
    return qs.stringify(param);
  }

  /** Sets the `Authorization: Bearer <token>` header from the token currently in local storage. */
  static addToken(): AxiosInstance {
    this.instance.defaults.headers.common["Authorization"] =
      "Bearer " + LocalStorageApp.getItem("pcp.token");
    return this.instance;
  }

  /** Sets the `content-type` header for form-encoded POST bodies (see {@link qsStringify}). */
  static addPostSettings(): AxiosInstance {
    this.instance.defaults.headers.common["content-type"] =
      "application/x-www-form-urlencoded";
    return this.instance;
  }

  static withUrl(url: string): AxiosInstance {
    this.instance.defaults.baseURL = url;
    return this.instance;
  }

  /**
   * POSTs to `{apiBase}/?task={task}` with a form-encoded payload, and pushes a toast based on
   * `response.error`/`response.success` — the standard way to call a mutating backend endpoint.
   * Prefer this over hand-rolling the URL + toast logic in a service method.
   * @param successMessage Fallback toast message when the backend doesn't provide its own `message`.
   * @param apiBase Defaults to `VUE_APP_API_APP`; pass `VUE_APP_API_AUTH` for auth endpoints.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static async postTask<T = any>(
    task: string,
    payload: Record<string, unknown> = {},
    successMessage?: string,
    apiBase: string | undefined = process.env.VUE_APP_API_APP
  ): Promise<TaskResponse<T>> {
    const targetUrl = `${apiBase}/?task=${task}`;
    await this.addPostSettings();
    const { data: response } = await this.instance.post<TaskResponse<T>>(
      targetUrl,
      this.qsStringify(payload)
    );
    if (response.error) {
      toast.pushError(response.message ?? "Une erreur s'est produite.");
    }
    if (response.success) {
      toast.createAndPushToast(
        response.message ?? successMessage ?? "OK",
        "",
        true,
        TYPE.SUCCESS
      );
    }
    return response;
  }
}
